import { reeferEquipmentTypes } from '@common/commonLists';
import { getCarrierMaxRateBlock } from '@common/order/validations';
import {
  isCreditLimitExceededByValues,
  isInNegativeCreditWarningZone,
} from '@common/order/creditLimit';
import { carrierStore, customerStore, orderStore } from '@store/orders/store';
import { Carrier } from '@store/orders/types';
import { userStore } from '@store/user';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';

import { computeTotalLineItems } from './lineItems';

export const getTrackingData = () =>
  orderStore.order.loads[orderStore.activeTab.index]?.loadTracking ?? [];

export const stops = () =>
  orderStore.order.loads[orderStore.activeTab.index].stops ?? [];

export const orderRepEmail: () => string | null = () =>
  orderStore.order.orderRep?.email ?? null;

export const load: () => ILoadEditViewModel | null = () =>
  orderStore.order.loads[orderStore.activeTab.index] ?? null;

export const index: () => number = () => orderStore.activeTab.index;

export const vendor: () => VendorContractViewModel | null = () =>
  orderStore.order.vendors?.[orderStore.activeTab.index] ?? null;

export const coveredByEmail: () => string | null = () => {
  return load()?.coveredByEmail ?? '';
};

export const isReefer = () => {
  return reeferEquipmentTypes.includes(load()?.equipment ?? '');
};

export const isTL = () => {
  return Boolean(load()?.mode === 'TL');
};

export const isPartial = () => {
  return Boolean(load()?.mode === 'Partial');
};

export const isCreate = () => {
  return orderStore.isCreate;
};

// New and copied orders skip the max rate check.
export const getOrderCarrierMaxRateBlock = (
  load: Parameters<typeof getCarrierMaxRateBlock>[0],
) => (orderStore.isCreate ? null : getCarrierMaxRateBlock(load));

export const carrier: () => Carrier | null = () => {
  return carrierStore.carrier[orderStore.activeTab.index];
};

export const totalLoads = () => {
  return carrierStore.carrier[orderStore.activeTab.index]?.totalLoads ?? 0;
};

export const isCarrierDirty = () => {
  return !!load()?.isCarrierDirty;
};

export const hasTrackingId = () => {
  return customerStore.customer.trackingXID !== '';
};

export const loadTrackingRequest = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return load()?.loadTrackingRequest ?? null;
};

export const aiTrackingStatus = () => {
  return load()?.aiTrackingStatus ?? null;
};

//Account Manager, Sales Manager, Order Rep, or Operations Rep
export const isRep = () => {
  return (
    orderStore.order.assignedId === userStore.user.id ||
    orderStore.order.operationsRep?.id === userStore.user.id ||
    customerStore.customer.accountManagerId === userStore.user.id ||
    customerStore.customer.salesManagerId === userStore.user.id
  );
};

export const orderContainsLTLLoad = (): boolean => {
  return orderStore.order.loads.some((load) => load.mode === 'LTL');
};

export const isCreditLimitExceeded = (): boolean => {
  return isCreditLimitExceededByValues({
    available:
      (orderStore.order.customer?.existingCustomerCredit ??
        customerStore.customer.available) +
      (orderStore.order.existingLineItemsTotal ?? 0),
    negativeCreditAllowance: customerStore.customer.negativeCreditAllowance,
    lineItemsTotal: computeTotalLineItems(),
  });
};

export const isOrderInNegativeCreditWarningZone = (): boolean => {
  return isInNegativeCreditWarningZone({
    available:
      (orderStore.order.customer?.existingCustomerCredit ??
        customerStore.customer.available) +
      (orderStore.order.existingLineItemsTotal ?? 0),
    negativeCreditAllowance: customerStore.customer.negativeCreditAllowance,
    lineItemsTotal: computeTotalLineItems(),
  });
};

export const isNegativeCreditLiabilityAccepted = (): boolean =>
  orderStore.order.negativeCreditLiabilityDecision?.userAcceptedLiability ===
  true;
